import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Copy, Megaphone, Trash2, Save } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/utils/roles";
import { AWARDS } from "@/components/t4r/CourseDialogs";
import { AnnouncementsDialog } from "@/components/t4r/AnnouncementsDialog";

export const CourseSettingsTab = ({ course, reload }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  useEffect(() => { setForm({ title: course.title, award: course.award || "", summary: course.summary || "", is_published: !!course.is_published, tutor_email: course.tutor_email || "" }); }, [course]);

  const save = async (patch = {}) => {
    const body = { ...form, ...patch };
    if (!body.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try { await api.put(`/lms/admin/courses/${course.id}`, body); toast.success("Course saved"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const duplicate = async () => {
    try { const { data } = await api.post(`/lms/admin/courses/${course.id}/duplicate`); toast.success(`Created "${data.title}" as a draft`); navigate(`/t4r/courses/${data.id}?tab=settings`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't duplicate"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${course.title}" with all lessons, quizzes and materials? This can't be undone.`)) return;
    try { await api.delete(`/lms/admin/courses/${course.id}`); toast.success("Course deleted"); navigate("/t4r/courses"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_320px]" data-testid="course-settings-tab">
      <div className="space-y-4 rounded-xl border border-border bg-white p-5">
        <div><Label className="text-xs">Course title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="settings-title" /></div>
        <div>
          <Label className="text-xs">Award / qualification</Label>
          <Input list="award-options" value={form.award || ""} onChange={(e) => setForm({ ...form, award: e.target.value })} placeholder="e.g. Level 3 Retrofit Assessor" data-testid="settings-award" />
          <datalist id="award-options">{AWARDS.map((a) => <option key={a} value={a} />)}</datalist>
        </div>
        <div><Label className="text-xs">Summary shown to learners</Label><Textarea value={form.summary || ""} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="min-h-[120px]" data-testid="settings-summary" /></div>
        <div>
          <Label className="text-xs">Tutor email for completion alerts</Label>
          <Input type="email" value={form.tutor_email || ""} onChange={(e) => setForm({ ...form, tutor_email: e.target.value })} placeholder="Leave blank to alert all Retrofit staff" data-testid="settings-tutor-email" />
          <p className="mt-1 text-[11px] text-muted-foreground">Gets an email each time a learner finishes every lesson and their certificate is issued.</p>
        </div>
        <Button className="gap-1 bg-green-700 text-white hover:bg-green-800" disabled={busy} onClick={() => save()} data-testid="settings-save"><Save size={14} /> Save changes</Button>
      </div>
      <aside className="space-y-3">
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-semibold">{form.is_published ? "Published" : "Draft"}</p><p className="text-[11px] text-muted-foreground">{form.is_published ? "Enrolled learners can see this course." : "Hidden from all learners until published."}</p></div>
            <Switch checked={!!form.is_published} disabled={busy} onCheckedChange={(v) => { setForm({ ...form, is_published: v }); save({ is_published: v }); }} data-testid="settings-published" />
          </div>
        </div>
        <div className="space-y-2 rounded-xl border border-border bg-white p-4">
          <Button variant="outline" className="w-full justify-start gap-2" onClick={() => setAnnouncing(true)} data-testid="settings-announce"><Megaphone size={14} /> Announcements</Button>
          <Button variant="outline" className="w-full justify-start gap-2" onClick={duplicate} data-testid="settings-duplicate"><Copy size={14} /> Duplicate course</Button>
          <p className="text-[11px] text-muted-foreground">Copies lessons, quizzes and materials into a new draft. Learners aren't copied.</p>
        </div>
        {isSuperAdmin(user?.role) && (
          <Button variant="outline" className="w-full justify-start gap-2 border-rose-200 text-rose-600 hover:bg-rose-50" onClick={remove} data-testid="settings-delete"><Trash2 size={14} /> Delete course</Button>
        )}
      </aside>
      <AnnouncementsDialog course={announcing ? course : null} onClose={() => setAnnouncing(false)} />
    </div>
  );
};
